import { parsePlaceholder } from '../utils/detectAnswer';

var FormulaParser = require('hot-formula-parser').Parser;
var parser = new FormulaParser();
parser.setFunction('equal', function (params) {
  return params.toString();
});

const V2_TO_V1_OPERATOR = {
  equal: 'EQUAL',
  not_equal: 'NOTEQUAL',
  contains: 'CONTAIN',
  not_contains: 'NOTCONTAIN',
  has_selected: 'HASSELECTED',
  has_not_selected: 'HASNOTSELECTED',
  greater_than: 'GREATERTHAN',
  less_than: 'LESSTHAN',
  greater_than_or_equal: 'GREATERTHANOREQUAL',
  less_than_or_equal: 'LESSTHANOREQUAL',
  empty: 'EMPTY',
  not_empty: 'NOTEMPTY',
  matches: 'MATCHES',
  not_matches: 'NOTMATCH',
};

export const v2ConditionsToV1Statement = (v2) => {
  if (!v2 || !v2.conditions) return v2;
  const conditions = v2.conditions.map(cond => {
    const thenClause = { Statement: { type: 'VALUE', content: cond.result } };
    if (!cond.rules || cond.rules.length === 0) {
      return { then: thenClause };
    }
    const operators = cond.rules.map(rule => ({
      logic: rule.logic ? rule.logic.toUpperCase() : undefined,
      key: rule.key || '',
      function: V2_TO_V1_OPERATOR[rule.operator] || (rule.operator || '').toUpperCase(),
      value: rule.value ?? '',
    }));
    return { operators, then: thenClause };
  });
  return { Statement: { type: 'IF', content: { Condition: conditions } } };
};

export const createDefaultCondition = (statement) => {
  if (!statement) {
    return;
  }

  if (statement.Statement) {
    statement = statement.Statement;
  }

  if (statement.type === 'IF') {
    //only for IF condition
    let condition = statement.content.Condition;

    if (condition) {
      let foundDefault = false;
      let lastValue = '';

      for (let a = 0; !foundDefault && a < condition.length; a++) {
        //going through all the condition and try to find the default value
        if (!condition[a].operators && condition[a].then.Statement) {
          foundDefault = true;
        } else {
          let val = handleValueStatement(condition[a].then.Statement);
          if (typeof val === 'boolean') {
            //only handle boolean for now
            lastValue = val;
          } else {
            lastValue = val === 'true';
          }
        }
      }

      if (!foundDefault) {
        //the default not found
        condition.push({
          then: {
            Statement: {
              type: 'VALUE',
              content: !lastValue,
            },
          },
        });
      }
    }
  }
};

export const isStatementCondition = (value) =>
  value && typeof value === 'object' && value.Statement;

export const resolveVisibility = (_this, component) => {
  const hasVisibility =
    component.visibility !== undefined && component.visibility !== null;
  const visibility = hasVisibility ? component.visibility : component.visible;

  if (hasVisibility || isStatementCondition(visibility)) {
    if (typeof visibility === 'boolean') {
      return visibility ? 'true' : 'false';
    }
    createDefaultCondition(visibility);
    return dynamicRender(_this, '', visibility) + '';
  }

  return component.visible === false ? 'false' : 'true';
};

export const normalizeConditionalProperties = (component) => {
  let normalized = component;

  if (normalized.visible && normalized.visible.conditions) {
    normalized = {
      ...normalized,
      visibility: v2ConditionsToV1Statement(normalized.visible),
    };
  }
  if (normalized.required && normalized.required.conditions) {
    normalized = {
      ...normalized,
      required: v2ConditionsToV1Statement(normalized.required),
    };
  }
  if (normalized.readOnly && normalized.readOnly.conditions) {
    normalized = {
      ...normalized,
      read_only: v2ConditionsToV1Statement(normalized.readOnly),
      readOnly: undefined,
    };
  }
  if (
    normalized.prefill &&
    typeof normalized.prefill === 'object' &&
    normalized.prefill.conditions
  ) {
    normalized = {
      ...normalized,
      auto: v2ConditionsToV1Statement(normalized.prefill),
      prefill: undefined,
    };
  }
  if (
    normalized.comment &&
    typeof normalized.comment === 'object' &&
    normalized.comment.conditions
  ) {
    normalized = {
      ...normalized,
      comment: v2ConditionsToV1Statement(normalized.comment),
    };
  }

  return normalized;
};

export const resolveDynamicBoolean = (_this, value, defaultValue = false) => {
  if (value === true || value === false) {
    return value;
  }
  if (value === undefined || value === null) {
    return defaultValue;
  }

  const resolved = dynamicRender(_this, defaultValue ? 'true' : 'false', value);
  return resolved === true || resolved === 'true';
};

export const hasMeaningfulOptions = (options) => {
  if (!Array.isArray(options)) {
    return options !== undefined && options !== null && options !== '';
  }

  return options.some((option) => {
    if (Array.isArray(option)) {
      return option.some(
        (value) => value !== undefined && value !== null && `${value}`.trim() !== ''
      );
    }

    if (option && typeof option === 'object') {
      return Object.values(option).some(
        (value) => value !== undefined && value !== null && `${value}`.trim() !== ''
      );
    }

    return option !== undefined && option !== null && `${option}`.trim() !== '';
  });
};

export const executeStatement = (statement, formData, defaultValue) => {
  if (!statement) {
    return defaultValue;
  }

  var result;
  if (statement.type === 'VALUE') {
    result = handleValueStatement(statement);
  } else if (statement.type === 'IF') {
    result = handleIFStatement(statement, formData, defaultValue);
  }

  return result;
};

const handleIFStatement = (statement, formData, defaultValue) => {
  let condition = statement.content.Condition;

  if (condition) {
    let toContinue = true;
    for (let a = 0; toContinue && a < condition.length; a++) {
      if (!condition[a].operators && condition[a].then.Statement) {
        //this is the default value
        toContinue = false;
        return executeStatement(
          condition[a].then.Statement,
          formData,
          defaultValue
        );
      }

      let operatorResult = evaluateConditionOperators(
        condition[a].operators,
        formData
      );

      if (operatorResult) {
        //true
        toContinue = false;
        return executeStatement(
          condition[a].then.Statement,
          formData,
          defaultValue
        );
      }
    }

    if (toContinue) {
      //we have gone through all the conditions but no result
      return defaultValue;
    }
  }
};

const evaluateConditionOperators = (operators, formData) => {
  let result = false;
  let toContinue = true;

  for (var a = 0; operators && toContinue && a < operators.length; a++) {
    let element = operators[a];
    if (!element.logic) {
      //no logic defined
      //this has to be element #1
      result = evaluateOperatorFunction(element, formData);
    } else {
      //there is a logic defined
      switch (element.logic) {
        case 'AND':
          if (result) {
            let thisResult = evaluateOperatorFunction(element, formData);
            result = result && thisResult;
          } else {
            //the previous result was false, no need to continue
            toContinue = false;
          }
          break;
        case 'OR':
          let thisResult = evaluateOperatorFunction(element, formData);
          result = result || thisResult;
          break;
      }
    }
  }

  return result;
};

const evaluateOperatorFunction = (operator, formData) => {
  let result = false;
  if (formData.answer_data[operator.key]) {
    var value = formData.answer_data[operator.key].answer;
    let realValue = '';

    if (Array.isArray(value)) {
      //array. it could be array of string or array of array
      for (var a = 0; a < value.length; a++) {
        let v = value[a];
        if (Array.isArray(v)) {
          realValue = realValue + v[0];
        } else {
          //assume it is a String
          realValue = realValue + v;
        }
      }
      value = realValue;
    }

    let expectedValue = operator.value;
    result = handleConditionalCases(operator, value, expectedValue, formData);
  } else {
    //no value, nothing to compare
    // result = false;
    var value = '';

    let expectedValue = operator.value;
    result = handleConditionalCases(operator, value, expectedValue, formData);
  }

  return result;
};

const handleConditionalCases = (operator, value, expectedValue, formData) => {
  value = parsePlaceholder(value, formData);
  expectedValue = parsePlaceholder(expectedValue, formData);

  let result = false;
  switch (operator.function) {
    case 'GREATERTHAN':
      if (isNaN(value) || isNaN(expectedValue)) result = value > expectedValue;
      else result = parseInt(value) > parseInt(expectedValue);
      break;
    case 'LESSTHAN':
      if (isNaN(value) || isNaN(expectedValue)) result = value < expectedValue;
      else result = parseInt(value) < parseInt(expectedValue);
      break;
    case 'CONTAIN':
      result = value.includes(expectedValue);
      break;
    case 'NOTCONTAIN':
      result = !value.includes(expectedValue);
      break;
    case 'EQUAL':
      result = value == expectedValue;
      break;
    case 'NOTEQUAL':
      result = value != expectedValue;
      break;
    case 'HASNOTSELECTED':
      result = !value.includes(expectedValue);
      //result = value != expectedValue;
      break;
    case 'HASSELECTED':
      result = value.includes(expectedValue);
      //result = value != expectedValue;
      break;
    case 'MATCHES':
      result = value.trim() === expectedValue.trim();
      break;
    case 'NOTMATCH':
      result = value.trim() !== expectedValue.trim();
      break;
    case 'GREATERTHANOREQUAL':
      result = parseInt(value) >= parseInt(expectedValue);
      break;
    case 'LESSTHANOREQUAL':
      result = parseInt(value) <= parseInt(expectedValue);
      break;
    case 'EMPTY':
      result = value.trim() === '';
      break;
    case 'NOTEMPTY':
      result = value.trim() !== '';
      break;
    default:
      result = value == expectedValue;
      break;
  }

  return result;
};

const handleValueStatement = (statement) => {
  if (!statement) {
    return null;
  }
  return statement.content;
};

export const dynamicRender = (_this, data_default, data_dynamic) => {
  if (
    typeof data_dynamic === 'undefined' ||
    data_dynamic === '' ||
    data_dynamic === null
  ) {
    return data_default;
  } else {
    try {
      if (data_dynamic && data_dynamic.Statement) {
        //new format
        let data = executeStatement(
          data_dynamic.Statement,
          _this.props.form,
          data_default
        );
        return data;
      } else {
        //assume old format
        let re = /\$\{[^}]+\}/g;
        let m;
        let patternMatched = false;
        while ((m = re.exec(data_dynamic))) {
          patternMatched = true;
          let key = m[0]
            .replace('${', '')
            .replace('}', '')
            .replace(/\.(answer|comment)$/, '');
          let rep = '';
          if (_this.props.form.answer_data[key]) {
            if (
              typeof _this.props.form.answer_data[key].answer[0] == 'object'
            ) {
              rep = `'`;
              console.log(key);
              for (let i in _this.props.form.answer_data[key].answer) {
                rep =
                  rep +
                  `${JSON.stringify(
                    _this.props.form.answer_data[key].answer[i]
                  )},`;
              }
              rep = rep.slice(0, -1) + `'`;
            } else {
              rep = `"${_this.props.form.answer_data[key].answer[0]}"`;
            }
          } else {
            rep = '';
            // return data_default;
          }

          data_dynamic = data_dynamic.replace(m[0], rep);
        }

        if (!patternMatched) {
          //the value is not the old dynamic formula pattern
          return data_dynamic;
        }

        const result = parser.parse(data_dynamic);
        console.log({ data_dynamic, result });
        if (result.error == null) {
          return result.result;
        } else {
          return data_default;
        }
      }
    } catch (e) {
      console.log(e);
      return data_default;
    }
  }
};
